/* global BigInt */
// Adjustment amounts are positive, GST-inclusive face amounts. Never add tax.
export const cents = (value) =>
  Math.round((Number(value || 0) + Number.EPSILON) * 100);
export const noteError = (message, statusCode = 400) =>
  Object.assign(new Error(message), { statusCode });
const decimal = (value, places, digits) => {
  const text = String(value ?? "");
  if (!new RegExp(`^\\d{1,${digits}}(?:\\.\\d{1,${places}})?$`).test(text))
    return null;
  const [whole, fraction = ""] = text.split(".");
  return (
    BigInt(whole) * 10n ** BigInt(places) + BigInt(fraction.padEnd(places, "0"))
  );
};
export function calculateNoteLines(lines = []) {
  if (!Array.isArray(lines) || !lines.length || lines.length > 200)
    return {
      valid: false,
      total: 0,
      lines: [],
      error: "Enter between 1 and 200 adjustment lines.",
    };
  let totalCents = 0;
  const result = [];
  for (const line of lines) {
    const quantity = decimal(line.quantity, 6, 7),
      unit = decimal(line.unitAmount, 2, 9);
    if (
      quantity === null ||
      unit === null ||
      quantity <= 0n ||
      unit <= 0n ||
      quantity > 1000000000000n
    )
      return {
        valid: false,
        total: 0,
        lines: [],
        error:
          "Enter a positive quantity and unit amount with no more than two decimal places.",
      };
    const face = Number((quantity * unit + 500000n) / 1000000n);
    totalCents += face;
    if (
      !Number.isSafeInteger(totalCents) ||
      totalCents > 100000000000000 ||
      face <= 0
    )
      return {
        valid: false,
        total: 0,
        lines: [],
        error: "The adjustment amount is outside the supported range.",
      };
    result.push({
      description: String(line.description || "").trim(),
      quantity: Number(quantity) / 1000000,
      unitAmount: Number(unit) / 100,
      total: face / 100,
    });
  }
  return { valid: true, total: totalCents / 100, lines: result };
}
export function validateNoteLines(lines) {
  const priced = calculateNoteLines(lines);
  if (!priced.valid) throw noteError(priced.error);
  if (
    priced.lines.some(
      (line) => !line.description || line.description.length > 2000
    )
  )
    throw noteError("Describe every adjustment line (up to 2,000 characters).");
  return priced;
}
export function inheritedNoteTax(
  lines,
  sourceFace,
  sourceGst,
  remainingGst = sourceGst,
  exhaustsAllowance = false
) {
  const face = lines.reduce((sum, line) => sum + cents(line.total), 0);
  const basis = Math.max(0, cents(sourceFace)),
    sourceTax = Math.max(0, cents(sourceGst));
  const availableTax = Math.max(0, cents(remainingGst));
  const tax = Math.min(
    face,
    availableTax,
    exhaustsAllowance
      ? availableTax
      : basis
      ? Number(
          (BigInt(face) * BigInt(sourceTax) + BigInt(Math.floor(basis / 2))) /
            BigInt(basis)
        )
      : 0
  );
  const allocated = lines.map((line, index) => {
    const product = BigInt(cents(line.total)) * BigInt(tax);
    return {
      index,
      tax: face ? Number(product / BigInt(face)) : 0,
      remainder: face ? product % BigInt(face) : 0n,
    };
  });
  let carry = tax - allocated.reduce((sum, line) => sum + line.tax, 0);
  const order = [...allocated].sort((a, b) =>
    a.remainder === b.remainder
      ? a.index - b.index
      : a.remainder > b.remainder
      ? -1
      : 1
  );
  for (const row of order)
    if (carry > 0 && allocated[row.index].tax < cents(lines[row.index].total)) {
      allocated[row.index].tax++;
      carry--;
    }
  return {
    totalGSTAmount: tax / 100,
    totalAmount: (face - tax) / 100,
    finalAmount: face / 100,
    lines: lines.map((line, index) => ({
      ...line,
      gstAmount: allocated[index].tax / 100,
    })),
  };
}
